import React, { useEffect } from 'react';
import { Button } from '../ui/button';

import checkSuccessImg from '@/assets/images/check-success.svg';

type ModalVariant = 'success' | 'failure' | 'confirm';

interface Step {
  title: string;
  message: string | React.ReactNode;
  img: string;
}

interface ExplanatoryModalProps {
  isOpen: boolean;
  variant?: ModalVariant;
  steps: Step[];
  currentStep: number;
  buttonText: string | React.ReactNode;
  onButtonClick: () => void;
  onClose?: () => void;
  onStepChange?: (step: number) => void;
}

const ExplanatoryModal: React.FC<ExplanatoryModalProps> = ({
  isOpen,
  variant = 'success',
  steps,
  currentStep,
  buttonText,
  onButtonClick,
  onClose = () => {},
  onStepChange
}) => {
  // Reset step when modal is opened
  useEffect(() => {
    if (isOpen && currentStep !== 0 && onStepChange) {
      onStepChange(0);
    }
  }, [isOpen, currentStep, onStepChange]);

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const getButtonColorClass = (variant: ModalVariant) => {
    switch (variant) {
      case 'success':
        return 'bg-primary hover:bg-primary-dark';
      case 'failure':
        return 'bg-red-500 hover:bg-red-600';
      default:
        return 'bg-primary hover:bg-primary-dark';
    }
  };

  const handleNextClick = () => {
    if (isLastStep) {
      onClose();
    } else {
      onButtonClick();
    }
  };

  return (
    <div className="fixed w-screen h-screen top-0 left-0 bg-[rgba(82,80,96,0.5)] backdrop-blur-[8px] z-50 flex items-center justify-center">
      <div className="bg-white rounded-[16px] overflow-hidden w-[327px]">
        <div className="w-full py-7 px-7 md:p-0 flex flex-col items-center justify-center gap-2">
          {/* Image */}
          <div className="md:bg-secondary w-full">
            <img className="w-full" src={currentStepData.img} alt={`step ${currentStep + 1}`} />
          </div>

          {/* Title */}
          <p className="md:px-7 font-bold text-xl text-center leading-[24.3px] md:mb-[0.15rem]">
            {currentStepData.title}
          </p>

          {/* Message */}
          <p className="md:px-7 text-sm text-secondary text-center leading-[17px]">{currentStepData.message}</p>
        </div>

        <div className="flex items-center justify-center gap-2 mt-14">
          {steps.map((_, index) => (
            <span
              key={index}
              className={`${index === currentStep ? 'w-[32px]' : 'w-[12px]'} h-[4px] rounded-[2px] ${
                index === currentStep ? 'bg-primary' : 'bg-[#DEE2E6]'
              }`}
            ></span>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex md:gap-4 md:px-5 md:mb-6 md:mt-7">
          <Button
            onClick={onClose}
            className={`flex-1 text-base font-semibold h-[52px] md:text-[1rem] md:font-semibold md:rounded-full md:h-[44px]`}
          >
            Cancel
          </Button>

          <Button
            onClick={handleNextClick}
            className={`text-base font-semibold h-[52px] md:text-[1rem] md:font-semibold md:rounded-full md:h-[44px] bg-lightRed rounded-none p-3 flex-1 hover:bg-red-500`}
          >
            {isLastStep ? 'Done' : buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExplanatoryModal;
